// OpenAI Provider - GPT-5.1 Implementation
// Based on OpenAI GPT-5.1 Cookbook
// Single clean implementation - NO delegation, NO structured output

import { getOrCreateDeviceId, getSessionId } from '../utils'

let currentAbortController = null

export function stopOpenAI() {
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }
}

/**
 * Main OpenAI call function
 * @param {Array} messages - Messages with role and content
 * @param {string|null} userApiKey - User's API key or null for server
 * @param {string} model - Model name (gpt-5.1 or gpt-5.1-codex)
 * @param {string} reasoningMode - 'none', 'low', 'medium', 'high' (GPT-5.1 uses 'none' not 'minimal')
 * @param {function} onThinking - Callback for real-time thinking
 * @returns {Promise<{content: string, reasoning: string, usage: object}>}
 */
export async function callOpenAI(messages, userApiKey, model = 'gpt-5.1', reasoningMode = 'low', onThinking = null) {
  currentAbortController = new AbortController()
  
  console.log('🟢 OpenAI API call:', { model, reasoningMode, hasKey: !!userApiKey })
  
  if (userApiKey) {
    return await callDirectAPI(messages, userApiKey, model, currentAbortController.signal, reasoningMode, onThinking)
  }
  
  return await callThroughServer(messages, model, reasoningMode, onThinking)
}

// Server streaming (through Cloudflare function)
async function callThroughServer(messages, model, reasoningMode, onThinking) {
  // Add retry logic for QUIC protocol errors
  let retries = 2
  let lastError = null
  
  // Get device ID for job persistence
  const deviceId = getOrCreateDeviceId()
  const sessionId = getSessionId()
  
  while (retries > 0) {
    try {
      const response = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          // Force HTTP/2 to avoid QUIC issues
          'Connection': 'keep-alive'
        },
        body: JSON.stringify({
          messages,
          provider: 'openai',
          model,
          reasoningMode,
          // Job persistence fields
          deviceId,
          sessionId
        }),
        signal: currentAbortController.signal,
        // Add cache bypass to avoid QUIC caching issues
        cache: 'no-store'
      })
      
      // If successful, continue with normal flow
      if (response.ok || response.status !== 0) {
        return await handleStreamingResponse(response, onThinking, deviceId)
      }
      
      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`)
    } catch (error) {
      console.error('Network error (attempt', 3 - retries + 1, '):', error)
      lastError = error
      
      // Check for QUIC protocol error
      if (error.message && error.message.includes('network')) {
        retries--
        if (retries > 0) {
          console.log('Retrying due to network error...')
          await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second
          continue
        }
      }
      break
    }
  }
  
  throw lastError || new Error('Network request failed')
}

async function handleStreamingResponse(response, onThinking, deviceId = null) {
  if (!response.ok) {
    const error = response.status === 504 
      ? '⏱️ Response timed out. Add your API key in Settings.'
      : await response.text() || 'OpenAI request failed'
    throw new Error(error)
  }

  if (!response.body) {
    throw new Error('Streaming not supported')
  }

  // Parse SSE stream
  let content = ''
  let reasoning = ''
  let jobId = null
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      
      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue
        
        const dataStr = line.slice(6)
        if (dataStr === '[DONE]') continue

        try {
          const data = JSON.parse(dataStr)
          
          // Capture job ID from server (for recovery)
          if (data.type === 'job_id' && data.id) {
            jobId = data.id
            // Store in localStorage for recovery on page reload
            if (deviceId) {
              try {
                localStorage.setItem('p5ai_active_job', JSON.stringify({
                  jobId,
                  deviceId,
                  startedAt: new Date().toISOString()
                }))
                console.log('[OpenAI] Job started:', jobId)
              } catch (e) {
                // Safari tracking prevention
              }
            }
          } else if (data.type === 'delta' && data.text) {
            content += data.text
          } else if (data.type === 'reasoning' && onThinking) {
            // Server sends reasoning deltas - pass them directly to callback
            const reasoningDelta = data.summary || ''
            if (reasoningDelta) {
              onThinking(reasoningDelta) // Pass delta, not full text
            }
          } else if (data.type === 'reasoning_complete' && onThinking) {
            // Final reasoning summary (if any)
            const finalSummary = data.summary || ''
            if (finalSummary) {
              // Don't overwrite - component accumulates deltas
            }
          } else if (data.type === 'error') {
            throw new Error(data.message || 'Stream error')
          }
        } catch (e) {
          console.warn('Parse error:', e)
        }
      }
    }
  } catch (error) {
    if (content.length > 0) {
      // Clear active job on partial success
      clearActiveJob()
      return { content, reasoning, usage: null, jobId }
    }
    throw error
  }

  // Clear active job on success
  clearActiveJob()

  if (!content) {
    throw new Error('No response received')
  }

  return { content, reasoning, usage: null, jobId }
}

// Clear active job from localStorage
function clearActiveJob() {
  try {
    localStorage.removeItem('p5ai_active_job')
  } catch (e) {
    // Safari tracking prevention
  }
}

// Direct API call (when user provides key)
async function callDirectAPI(messages, apiKey, model, signal, reasoningMode, onThinking) {
  const systemMsg = messages.find(m => m.role === 'system')
  const userMessages = messages.filter(m => m.role !== 'system')
  
  // Format for Responses API
  let input = ''
  if (systemMsg) {
    input += systemMsg.content + '\n\n'
  }
  
  userMessages.forEach(msg => {
    input += `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}\n\n`
  })

  const requestBody = {
    model,
    input: input.trim(),
    max_output_tokens: 100000,
    instructions: 'You are a p5.js coding assistant. Generate p5.js-compatible code.'
  }

  // Add reasoning only if not 'none' (GPT-5.1 uses 'none' instead of 'minimal')
  if (reasoningMode !== 'none') {
    requestBody.reasoning = { effort: reasoningMode }
  }

  const response = await fetch('https://api.openai.com/v1/responses', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify(requestBody),
    signal
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`OpenAI API error: ${error}`)
  }

  const data = await response.json()
  
  // Extract output
  let content = ''
  let reasoning = data.reasoning_summary || ''
  
  if (reasoning && onThinking) {
    onThinking(reasoning)
  }
  
  // Handle various response formats
  if (data.output) {
    if (typeof data.output === 'string') {
      content = data.output
    } else if (Array.isArray(data.output)) {
      for (const item of data.output) {
        if (item.content) {
          for (const part of item.content) {
            if (part.text) content += part.text
          }
        }
      }
    }
  }
  
  // Fallback
  if (!content && data.output_text) {
    content = data.output_text
  }

  return { content, reasoning, usage: data.usage }
}
